/**
 * Hydrates admin-inserted collapsible content ("accordion") blocks in CMS body
 * HTML. The Body editor stores them as:
 *
 *   <div data-accordion>
 *     <div data-accordion-item data-label="Section 1">…rich content…</div>
 *   </div>
 *
 * Each item becomes a full-width header button with its content in a panel
 * underneath. Everything starts collapsed; clicking a header expands that
 * section with a height animation, clicking it again collapses it. Sections
 * toggle independently, so several can be open at once.
 *
 * Content is inline (already in the page HTML, sanitized server-side on save),
 * so nothing is fetched when a section opens.
 */
// Must match the *expand* transition on .accordion-item.is-open .accordion-panel
// in styles.css (0.6s) — the panel switches to height:auto once it finishes.
const DURATION = 620;

function collapse(panel) {
  // Pin the current height, force a synchronous reflow so the browser has a
  // start value to animate from, then go to 0. (A rAF callback would be
  // skipped in a hidden/throttled tab and leave the panel stuck open.)
  panel.style.height = `${panel.scrollHeight}px`;
  void panel.offsetHeight;
  panel.style.height = '0px';
}

function expand(panel) {
  panel.style.height = '0px';
  void panel.offsetHeight;
  panel.style.height = `${panel.scrollHeight}px`;
  window.setTimeout(() => {
    // Let it size to its content afterwards, so nested/late content still fits.
    if (panel.dataset.open === '1') panel.style.height = 'auto';
  }, DURATION);
}

export function hydrateAccordions(root) {
  if (!root) return;

  root.querySelectorAll('div[data-accordion]').forEach((block) => {
    if (block.dataset.accHydrated) return;
    block.dataset.accHydrated = '1';
    block.classList.add('accordion');

    const items = [...block.querySelectorAll(':scope > div[data-accordion-item]')];
    if (!items.length) return;

    items.forEach((item) => {
      const label = item.getAttribute('data-label') || 'Section';

      // Move the item's existing content into a collapsible panel.
      const panel = document.createElement('div');
      panel.className = 'accordion-panel';
      panel.style.height = '0px';
      panel.dataset.open = '0';
      while (item.firstChild) panel.appendChild(item.firstChild);

      const inner = document.createElement('div');
      inner.className = 'accordion-panel-inner';
      while (panel.firstChild) inner.appendChild(panel.firstChild);
      panel.appendChild(inner);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'accordion-head';
      btn.setAttribute('aria-expanded', 'false');

      const text = document.createElement('span');
      text.className = 'accordion-label';
      text.textContent = label;
      const icon = document.createElement('span');
      icon.className = 'accordion-icon';
      icon.setAttribute('aria-hidden', 'true');
      btn.append(text, icon);

      btn.addEventListener('click', () => {
        const isOpen = panel.dataset.open === '1';
        panel.dataset.open = isOpen ? '0' : '1';
        item.classList.toggle('is-open', !isOpen);
        btn.setAttribute('aria-expanded', String(!isOpen));
        if (isOpen) collapse(panel); else expand(panel);
      });

      item.classList.add('accordion-item');
      item.append(btn, panel);
    });
  });
}
